import React, { useMemo } from 'react';
import { View, FlatList, RefreshControl, Platform } from 'react-native';
import { LinkedAccount } from '@/types/residents/home';
import { useResponsive } from '@/hooks/common/useResponsive';
import { LinkedAccountsEmpty } from './LinkedAccountsEmpty';
import { COLORS } from '@/constants/residents/homeConstants';
import { DesktopLinkedAccountCard } from '@/components/residents/linkedAccounts/DesktopLinkedAccountCard';
import { LinkedAccountListItem } from '@/components/residents/linkedAccounts/LinkedAccountListItem';

type LinkedAccountsListProps = {
  accounts: LinkedAccount[];
  isLoading: boolean;
  onRefresh: () => void;
  onRemove: (account: LinkedAccount) => void;
  contentContainerStyle?: any;
  listStyle?: any;
  desktopItemWrapperStyle?: any;
};

/**
 * Shared list for desktop and mobile. Keeps existing item renderers and styles identical.
 * It only centralizes the FlatList/Empty/Refresh logic.
 */
export const LinkedAccountsList: React.FC<LinkedAccountsListProps> = ({
  accounts,
  isLoading,
  onRefresh,
  onRemove,
  contentContainerStyle,
  listStyle,
  desktopItemWrapperStyle,
}) => {
  const { layoutMode, isWeb, isDesktop } = useResponsive();
  const isDesktopLayout = layoutMode === 'desktop' || (isWeb && isDesktop);

  const renderItem = useMemo(() => {
    return ({ item }: { item: LinkedAccount }) => {
      if (isDesktopLayout) {
        return (
          <View style={desktopItemWrapperStyle}>
            <DesktopLinkedAccountCard account={item} onRemove={onRemove} />
          </View>
        );
      }
      return (
        <LinkedAccountListItem linkedAccount={item} onRemove={onRemove} />
      );
    };
  }, [isDesktopLayout, onRemove, desktopItemWrapperStyle]);

  return (
    <FlatList
      data={accounts}
      renderItem={renderItem}
      keyExtractor={(item) => String(item.id)}
      style={listStyle}
      contentContainerStyle={contentContainerStyle}
      refreshControl={
        <RefreshControl
          refreshing={isLoading}
          onRefresh={onRefresh}
          tintColor={COLORS.primary}
        />
      }
      ListEmptyComponent={<LinkedAccountsEmpty />}
      showsVerticalScrollIndicator={false}
      scrollEventThrottle={16}
      decelerationRate="normal"
    />
  );
};


